import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * The app's brand slot (sidebar, and the phone top bar): the business's own logo when it has uploaded one, the Scopie mark when it has not.
 *
 * - The logo sits in a neutral white chip (object-contain, a fixed slot), so a transparent PNG still reads on the dark sidebar and nothing is inverted.
 * - Alt text is the business name; if the image cannot load, the Scopie mark takes over.
 * - While the organization is loading the slot is held empty at the same size: no layout shift, and no Scopie mark flashing before the logo.
 * - Someone who cannot read the organization (an estimator) simply gets the Scopie mark.
 */
export function BusinessBrand({
  organization,
  loading = false,
  variant,
  fallbackSrc,
  fallbackAlt,
  className,
}: {
  organization: { name?: string | null; logo_url?: string | null } | undefined;
  loading?: boolean;
  /** `wordmark`: the sidebar slot. `icon`: the square phone slot. */
  variant: 'wordmark' | 'icon';
  fallbackSrc: string;
  fallbackAlt: string;
  className?: string;
}) {
  const logo = organization?.logo_url?.trim() || null;
  const [failed, setFailed] = useState(false);
  // a different logo (replaced, removed, added) gets a fresh try
  useEffect(() => setFailed(false), [logo]);
  const icon = variant === 'icon';
  const slot = icon ? 'h-8 w-8' : 'h-10 w-36';

  if (loading && !organization) return <span data-testid="brand-placeholder" aria-hidden className={cn('block shrink-0 rounded-md bg-white/10', slot, className)} />;
  if (logo && !failed) {
    return (
      <span className={cn('flex shrink-0 items-center justify-center rounded-md bg-white', icon ? 'h-8 w-8 p-0.5' : 'h-10 max-w-[9rem] px-2', className)}>
        <img src={logo} alt={organization?.name || fallbackAlt} onError={() => setFailed(true)} className={cn('object-contain', icon ? 'h-7 w-7' : 'max-h-8 max-w-[8.5rem]')} />
      </span>
    );
  }
  return <img src={fallbackSrc} alt={fallbackAlt} className={cn(icon ? 'h-8 w-8' : 'h-8', className)} />;
}
